<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import ReportsAPI from 'dashboard/api/reports';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import RecordingReportsDownloadDialog from './RecordingReportsDownloadDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const loading = ref(false);
const isBulkDownloading = ref(false);
const isOpeningViewer = ref(false);
const recordings = ref([]);
const totalCount = ref(0);
const currentPage = ref(1);
const selectedAgents = ref([]);
const selectedRecordingIds = ref([]);
const hasInitializedFilters = ref(false);
const viewerDialogRef = ref(null);
const downloadDialogRef = ref(null);
const viewerSourceUrl = ref('');
const viewerMimeType = ref('');
const viewerFileName = ref('');
const viewerItem = ref(null);

const perPage = 20;
const filters = ref({
  agentIds: [],
});

const columns = computed(() => [
  { key: 'select', title: t('RECORDING_REPORTS.TABLE.HEADER.SELECT') },
  { key: 'room', title: t('RECORDING_REPORTS.TABLE.HEADER.ROOM') },
  { key: 'agent', title: t('RECORDING_REPORTS.TABLE.HEADER.AGENT') },
  { key: 'date', title: t('RECORDING_REPORTS.TABLE.HEADER.DATE') },
  { key: 'actions', title: t('RECORDING_REPORTS.TABLE.HEADER.ACTIONS') },
]);

const conversationLabel = item => {
  if (item?.conversation_id) {
    return `${t('RECORDING_REPORTS.CONVERSATION_LABEL')} #${item.conversation_id}`;
  }

  return item?.room_name || '--';
};

const conversationPath = item => {
  if (!item?.conversation_id) {
    return null;
  }

  return frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: item.conversation_id,
    })
  );
};

const agentOptions = computed(() => store.getters['agents/getAgents'] || []);
const downloadableRecordingIds = computed(() =>
  recordings.value.filter(item => item.download_url).map(item => item.id)
);
const selectedDownloadableRecordingIds = computed(() =>
  selectedRecordingIds.value.filter(recordingId =>
    downloadableRecordingIds.value.includes(recordingId)
  )
);

const formatDate = value => {
  if (!value) return '--';
  return moment(value).format('DD/MM/YYYY HH:mm:ss');
};

const openResolvedUrl = async (resolver, target = '_blank') => {
  try {
    const response = await resolver();
    const url = response.data?.url;

    if (!url) {
      throw new Error(t('RECORDING_REPORTS.ERRORS.MISSING_URL'));
    }

    if (target === '_self') {
      window.location.assign(url);
      return;
    }

    window.open(url, target, 'noopener,noreferrer');
  } catch {
    useAlert(t('RECORDING_REPORTS.ERRORS.OPEN_FAILED'));
  }
};

const isAudioRecording = recording => {
  const mimeType = String(recording?.mime_type || '').toLowerCase();
  if (mimeType.startsWith('audio/')) {
    return true;
  }

  return /\.(mp3|wav|ogg|m4a|aac|webm)(?:$|[?#])/i.test(
    recording?.file_name || recording?.download_url || ''
  );
};

const archiveFilenameFromHeaders = headers => {
  const contentDisposition = headers?.['content-disposition'] || '';
  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1]);
  }

  const plainMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return `gravacoes-salas-${moment().format('YYYY-MM-DD_HH-mm-ss')}.tar.gz`;
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const toggleRecordingSelection = recordingId => {
  if (!recordingId) {
    return;
  }

  if (selectedRecordingIds.value.includes(recordingId)) {
    selectedRecordingIds.value = selectedRecordingIds.value.filter(
      id => id !== recordingId
    );
    return;
  }

  selectedRecordingIds.value = [...selectedRecordingIds.value, recordingId];
};

const resetSelection = () => {
  selectedRecordingIds.value = [];
};

const loadRecordings = async () => {
  loading.value = true;

  try {
    const response = await ReportsAPI.getRecordingReports({
      page: currentPage.value,
      perPage,
      agentIds: filters.value.agentIds,
    });
    recordings.value = response.data?.recordings || [];
    totalCount.value = response.data?.pagination?.total_count || 0;
    resetSelection();
  } catch {
    recordings.value = [];
    totalCount.value = 0;
    resetSelection();
  } finally {
    loading.value = false;
  }
};

const handlePageChange = page => {
  currentPage.value = page;
  loadRecordings();
};

const openDownloadDialog = () => {
  downloadDialogRef.value?.open();
};

const onDownloadDialogConfirm = async ({
  useSelectedRecordingsOnly,
  from,
  to,
  agentIds,
}) => {
  if (isBulkDownloading.value) {
    return;
  }

  isBulkDownloading.value = true;

  try {
    const response = await ReportsAPI.downloadRecordingReports({
      from,
      to,
      agentIds,
      recordingIds: useSelectedRecordingsOnly
        ? selectedDownloadableRecordingIds.value
        : [],
    });

    const fileName = archiveFilenameFromHeaders(response.headers);
    const blob = new Blob([response.data], {
      type: response.headers?.['content-type'] || 'application/gzip',
    });

    downloadBlob(blob, fileName);
    downloadDialogRef.value?.close();
  } catch {
    useAlert(t('RECORDING_REPORTS.ERRORS.DOWNLOAD_ALL_FAILED'));
  } finally {
    isBulkDownloading.value = false;
  }
};

const openViewerModal = async item => {
  if (!item?.play_url || isOpeningViewer.value) {
    return;
  }

  isOpeningViewer.value = true;
  try {
    const response = await ReportsAPI.getRecordingPlayUrl(item.id);
    const url = response.data?.url;

    if (!url) {
      throw new Error(t('RECORDING_REPORTS.ERRORS.MISSING_URL'));
    }

    viewerSourceUrl.value = url;
    viewerMimeType.value = item.mime_type || '';
    viewerFileName.value = item.file_name || '';
    viewerItem.value = item;
    viewerDialogRef.value?.open();
  } catch {
    useAlert(t('RECORDING_REPORTS.ERRORS.OPEN_FAILED'));
  } finally {
    isOpeningViewer.value = false;
  }
};

const closeViewerModal = () => {
  viewerSourceUrl.value = '';
  viewerMimeType.value = '';
  viewerFileName.value = '';
  viewerItem.value = null;
};

const downloadRecording = item => {
  openResolvedUrl(() => ReportsAPI.getRecordingDownloadUrl(item.id), '_self');
};

watch(
  selectedAgents,
  agents => {
    if (!hasInitializedFilters.value) {
      return;
    }

    filters.value.agentIds = (agents || []).map(agent => agent.id);
    currentPage.value = 1;
    loadRecordings();
  },
  { deep: true }
);

onMounted(async () => {
  await store.dispatch('agents/get');
  await loadRecordings();
  hasInitializedFilters.value = true;
});
</script>

<template>
  <div class="p-4 space-y-4">
    <div class="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
      <label class="flex w-full max-w-xl flex-col gap-1 text-sm text-n-slate-11">
        {{ t('RECORDING_REPORTS.FILTERS.AGENTS') }}
        <multiselect
          v-model="selectedAgents"
          class="no-margin"
          :options="agentOptions"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="t('RECORDING_REPORTS.FILTERS.AGENTS_PLACEHOLDER')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        />
      </label>
      <Button
        slate
        sm
        icon="i-ph-download-simple"
        :label="t('RECORDING_REPORTS.ACTIONS.DOWNLOAD_ALL')"
        :disabled="loading"
        @click="openDownloadDialog"
      />
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-slate-3 bg-n-alpha-1">
      <table class="w-full min-w-[56rem] text-left">
        <thead
          class="border-b border-n-slate-3 bg-n-alpha-2 text-xs uppercase tracking-wide text-n-slate-10"
        >
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[
                'px-4 py-3',
                column.key === 'select' ? 'w-12 text-center' : '',
              ]"
            >
              <template v-if="column.key === 'select'">
                <span class="sr-only">{{
                  t('RECORDING_REPORTS.ACTIONS.SELECT_RECORDING')
                }}</span>
              </template>
              <template v-else>
                {{ column.title }}
              </template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="columns.length" class="px-4 py-6 text-sm text-n-slate-11">
              {{ t('RECORDING_REPORTS.LOADING') }}
            </td>
          </tr>
          <tr v-else-if="!recordings.length">
            <td :colspan="columns.length" class="px-4 py-6 text-sm text-n-slate-11">
              {{ t('RECORDING_REPORTS.EMPTY') }}
            </td>
          </tr>
          <tr
            v-for="item in recordings"
            v-else
            :key="item.id"
            class="border-b border-n-slate-3 text-sm text-n-slate-12"
          >
            <td class="w-12 px-4 py-3 text-center align-middle">
              <label
                v-if="item.download_url"
                class="inline-flex cursor-pointer items-center justify-center"
              >
                <span class="sr-only">{{
                  t('RECORDING_REPORTS.ACTIONS.SELECT_RECORDING')
                }}</span>
                <Checkbox
                  :model-value="selectedRecordingIds.includes(item.id)"
                  @change="() => toggleRecordingSelection(item.id)"
                />
              </label>
            </td>
            <td class="px-4 py-3 font-medium">
              <a
                v-if="conversationPath(item)"
                :href="conversationPath(item)"
                class="text-woot-500 hover:underline"
              >
                {{ conversationLabel(item) }}
              </a>
              <span v-else>{{ conversationLabel(item) }}</span>
            </td>
            <td class="px-4 py-3">{{ item.initiated_by || '--' }}</td>
            <td class="px-4 py-3">{{ formatDate(item.started_at) }}</td>
            <td class="px-4 py-3">
              <div class="flex items-center gap-3">
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-n-slate-3 text-n-slate-11 transition hover:border-n-brand hover:text-n-brand disabled:cursor-not-allowed disabled:opacity-50"
                  :title="t('RECORDING_REPORTS.ACTIONS.VIEW')"
                  :disabled="!item.play_url || isOpeningViewer"
                  @click="openViewerModal(item)"
                >
                  <span class="i-ri-eye-line text-base" />
                </button>

                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-n-slate-3 text-n-slate-11 transition hover:border-n-brand hover:text-n-brand disabled:cursor-not-allowed disabled:opacity-50"
                  :title="t('RECORDING_REPORTS.ACTIONS.DOWNLOAD')"
                  :disabled="!item.download_url"
                  @click="downloadRecording(item)"
                >
                  <span class="i-ri-download-2-line text-base" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <PaginationFooter
      :current-page="currentPage"
      :total-count="totalCount"
      :page-size="perPage"
      @page-change="handlePageChange"
    />

    <RecordingReportsDownloadDialog
      ref="downloadDialogRef"
      :initial-selected-agents="selectedAgents"
      :selected-recording-count="selectedDownloadableRecordingIds.length"
      :is-downloading="isBulkDownloading"
      @download="onDownloadDialogConfirm"
    />

    <Dialog
      ref="viewerDialogRef"
      width="2xl"
      :show-confirm-button="false"
      :cancel-button-label="t('RECORDING_REPORTS.VIEWER.CLOSE')"
      @close="closeViewerModal"
    >
      <template #title>
        <a
          v-if="viewerItem && conversationPath(viewerItem)"
          :href="conversationPath(viewerItem)"
          class="text-woot-500 hover:underline"
        >
          {{ conversationLabel(viewerItem) }}
        </a>
        <span v-else-if="viewerItem">{{ conversationLabel(viewerItem) }}</span>
        <span v-else>{{ t('RECORDING_REPORTS.VIEWER.TITLE') }}</span>
      </template>
      <div class="w-full">
        <audio
          v-if="
            viewerSourceUrl &&
            isAudioRecording({
              mime_type: viewerMimeType,
              file_name: viewerFileName,
            })
          "
          class="w-full"
          controls
          preload="none"
          :src="viewerSourceUrl"
        />
        <video
          v-else-if="viewerSourceUrl"
          class="w-full rounded-lg border border-n-slate-3"
          controls
          preload="metadata"
          :src="viewerSourceUrl"
        />
      </div>
    </Dialog>
  </div>
</template>
